import { useState, useEffect, useMemo, Component, type ReactNode } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Grid } from '@react-three/drei';
import { calculateBoundingBox, gridScale, parseGCode } from './utils.ts';
import GCodeRenderer from './Renderer.tsx';
import CenteredCamera from './CenteredCamera.tsx';
import { getPublicFileFromS3 } from '@/lib/s3.ts';

// ponytail: stdlib React boundary so a WebGL context failure degrades to a
// message instead of crashing the whole route. Upgrade to react-error-boundary
// if a retry button or reuse across viewers is ever needed.
class WebGLBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    const { failed } = this.state;
    const { children } = this.props;
    if (failed) {
      return (
        <h1>3D preview unavailable (WebGL is disabled in your browser).</h1>
      );
    }
    return children;
  }
}

interface ModelViewerProps {
  modelURL: string;
  enableGrid?: boolean;
  backgroundColor?: string;
  gridColor?: string;
  modelColor?: string;
  boundingBoxColor?: string;
  dTextColor?: string;
  // 'demand' renders only when the camera moves — the model is static, so a
  // continuous loop just pins the main thread. Defaults to 'always' so existing
  // callers are unchanged; opt in where idle cost matters.
  frameloop?: 'always' | 'demand';
}

function Viewer({
  modelURL,
  enableGrid = true,
  backgroundColor = 'lightgray',
  gridColor = '#696969',
  modelColor = 'green',
  boundingBoxColor = 'gray',
  dTextColor = 'black',
  frameloop = 'always',
}: ModelViewerProps) {
  const [gcode, setGcode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchGCode = async () => {
      try {
        setLoading(true);
        const response = await getPublicFileFromS3(modelURL);
        if (response.ok) {
          const gcodeText = await response.text();
          setGcode(gcodeText);
        } else {
          throw new Error('Failed to Load the model.');
        }
        setLoading(false);
      } catch {
        setLoading(false);
        setError('Failed to Load the model.');
      }
    };

    fetchGCode();
  }, [modelURL]);

  // Parse G-code once using memoization to avoid re-parsing on every render
  // Note: GCodeRenderer also parses, but both use memoization so it's efficient
  const boundingBox = useMemo(() => {
    if (!gcode) return null;
    const { rapidMovePoints, linearMovePoints } = parseGCode(gcode);
    const allPoints = [...rapidMovePoints, ...linearMovePoints];
    return calculateBoundingBox(allPoints);
  }, [gcode]);

  if (loading) {
    return <h1>Loading...</h1>;
  }

  if (error) {
    return <h1>{error}</h1>;
  }

  return (
    <WebGLBoundary>
      <Canvas
        frameloop={frameloop}
        camera={{ fov: 45 }}
        style={{ background: backgroundColor, borderRadius: '20px' }}
      >
        {boundingBox && <CenteredCamera boundingBox={boundingBox} />}

        <OrbitControls maxPolarAngle={Math.PI} />

        {enableGrid && (
          <Grid
            args={[150, 150]}
            rotation={[Math.PI / 2, 0, 0]}
            sectionColor={gridColor}
            infiniteGrid
            {...gridScale(boundingBox)}
          />
        )}

        {gcode && (
          <GCodeRenderer
            gcode={gcode}
            modelColor={modelColor}
            boundingBoxColor={boundingBoxColor}
            dTextColor={dTextColor}
          />
        )}
      </Canvas>
    </WebGLBoundary>
  );
}

Viewer.defaultProps = {
  backgroundColor: 'lightgray',
  gridColor: '#696969',
  modelColor: 'green',
  boundingBoxColor: 'gray',
  enableGrid: true,
  dTextColor: 'black',
  frameloop: 'always',
};

export default Viewer;
